import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShoppingBag, Plus, MessageCircle, Search, Zap, Sparkles, Flag, Pause, Play, CheckCircle2, CreditCard, BadgeCheck, Star, Truck, Download } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";
import { WHATSAPP_NUMBER } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["all", "services", "digital", "fashion", "food", "electronics", "ajo", "other"];

export default function MarketplacePage() {
  const { user } = useAuth();
  const cur = user?.preferred_currency || "GBP";
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [orders, setOrders] = useState({ bought: [], sold: [] });
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("newest");
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "services", price: 10, description: "", whatsapp_number: WHATSAPP_NUMBER, location: "", image_url: "" });
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const initialQ = params.get("q");
    if (initialQ) setQ(initialQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return from Stripe (boost purchase)
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid" && data.credited) {
          setParams({}, { replace: true });
          toast.success(data.kind === "marketplace" ? "✅ Order paid — the seller has been notified!" : "Listing boosted for 7 days");
          load(); return;
        }
      } catch (err) { /* retry */ }
      attempts += 1;
      if (attempts < 10) setTimeout(poll, 2000);
      else setParams({}, { replace: true });
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const params = new URLSearchParams();
    if (cat !== "all") params.set("category", cat);
    if (q) params.set("q", q);
    params.set("sort", sort);
    const { data } = await api.get(`/marketplace/listings?${params.toString()}`);
    setItems(data);
    if (user) {
      try { const { data: m } = await api.get("/marketplace/my-listings"); setMine(m.filter((x) => x.status !== "hidden_test")); } catch {}
      try { const { data: o } = await api.get("/marketplace/orders/mine"); setOrders(o); } catch {}
    }
  };

  const orderAction = async (order, action) => {
    try {
      await api.post(`/marketplace/orders/${order.order_id}/${action}`);
      toast.success(action === "deliver" ? "Marked delivered — buyer notified 📦" : "Order completed ✅");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/marketplace/orders/${reviewFor.order_id}/review`, { rating, comment });
      toast.success("Review posted — thank you ⭐");
      setReviewFor(null); setComment(""); setRating(5); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, sort, q]);

  const setStatus = async (item, status) => {
    try {
      await api.patch(`/marketplace/listings/${item.listing_id}`, { status });
      toast.success(status === "active" ? "Listing re-activated" : status === "paused" ? "Listing paused" : "Marked as sold 🎉");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const report = async (item) => {
    const reason = window.prompt("Why are you reporting this listing? (spam, scam, prohibited item, misleading…)");
    if (!reason || reason.trim().length < 3) return;
    try {
      const { data } = await api.post(`/marketplace/listings/${item.listing_id}/report`, { reason: reason.trim() });
      toast.success(data.message);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send report"); }
  };

  const buyNow = async (item) => {
    try {
      const { data } = await api.post(`/marketplace/listings/${item.listing_id}/buy`, {
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start checkout"); }
  };

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [vStatus, setVStatus] = useState("unverified");
  const [vForm, setVForm] = useState({ full_name: "", date_of_birth: "", address: "", business_name: "" });
  useEffect(() => {
    if (!user) return;
    api.get("/marketplace/seller/verification").then((r) => setVStatus(r.data.status)).catch(() => {});
  }, [user]);
  const submitVerify = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/marketplace/seller/verify", vForm);
      toast.success(data.message);
      setVStatus("pending"); setVerifyOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/marketplace/listings", { ...form, price: Number(form.price) });
      toast.success("Listing created");
      setNewOpen(false);
      setForm({ title: "", category: "services", price: 10, description: "", whatsapp_number: WHATSAPP_NUMBER, location: "", image_url: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const contact = (item) => {
    const msg = `Hi! I'm interested in your "${item.title}" listing on Zuntria (${formatMoney(item.price, cur)}).`;
    window.open(`https://wa.me/${item.whatsapp_number}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const boost = async (item) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "boost_listing",
        origin_url: window.location.origin + "/marketplace",
        kind: "service",
        extra: { listing_id: item.listing_id },
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start boost");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Marketplace</p>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Browse & sell</h1>
        </div>
        <div className="flex gap-2">
          {user && vStatus !== "verified" && (
            <Button variant="outline" onClick={() => vStatus === "pending" ? toast.info("Verification under review") : setVerifyOpen(true)} data-testid="get-verified-btn">
              <BadgeCheck className="mr-1 h-4 w-4 text-primary" /> {vStatus === "pending" ? "Verification pending" : "Get verified"}
            </Button>
          )}
          {user && vStatus === "verified" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-2 text-xs font-medium text-primary" data-testid="verified-pill">
              <BadgeCheck className="h-4 w-4" /> Verified seller
            </span>
          )}
          <Button onClick={() => setNewOpen(true)} data-testid="market-new-btn"><Plus className="mr-1 h-4 w-4" /> List something</Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search…" className="h-10 pl-9" data-testid="market-search" />
        </div>
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`market-cat-${c}`}
              className={`rounded-full px-3 py-1 text-xs capitalize transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/80"}`}>
              {c}
            </button>
          ))}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-10 w-[150px]" data-testid="market-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="price_asc">Price: low → high</SelectItem>
            <SelectItem value="price_desc">Price: high → low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(orders.bought.length > 0 || orders.sold.length > 0) && (
        <div className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="my-orders-section">
          <h2 className="font-display text-lg">My orders</h2>
          {orders.sold.length > 0 && (
            <>
              <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">Sales to fulfil</p>
              <ul className="divide-y divide-border">
                {orders.sold.map((o) => (
                  <li key={o.order_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`sold-order-${o.order_id}`}>
                    <span className="min-w-0 truncate">{o.title} · £{Number(o.amount).toFixed(2)} · buyer {o.buyer_name}</span>
                    <Button size="sm" onClick={() => orderAction(o, "deliver")} data-testid={`deliver-${o.order_id}`}>
                      <Truck className="mr-1 h-3.5 w-3.5" /> Mark delivered
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {orders.bought.length > 0 && (
            <>
              <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">Purchases</p>
              <ul className="divide-y divide-border">
                {orders.bought.map((o) => (
                  <li key={o.order_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`bought-order-${o.order_id}`}>
                    <span className="min-w-0 truncate">{o.title} · £{Number(o.amount).toFixed(2)}
                      <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{o.status}</span>
                    </span>
                    <span className="flex gap-2">
                      {o.digital && ["paid", "delivered", "completed"].includes(o.status) && (
                        <Button size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/orders/${o.order_id}/download`, "_blank")} data-testid={`download-${o.order_id}`}>
                          <Download className="mr-1 h-3.5 w-3.5" /> Download
                        </Button>
                      )}
                      {o.status === "delivered" && (
                        <Button size="sm" onClick={() => orderAction(o, "confirm")} data-testid={`confirm-${o.order_id}`}>
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Confirm received
                        </Button>
                      )}
                      {o.status === "completed" && (
                        <Button size="sm" variant="outline" onClick={() => setReviewFor(o)} data-testid={`review-${o.order_id}`}>
                          <Star className="mr-1 h-3.5 w-3.5 text-amber-400" /> Review
                        </Button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {mine.length > 0 && (
        <div className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="my-listings-section">
          <h2 className="font-display text-lg">My listings</h2>
          <ul className="mt-3 divide-y divide-border">
            {mine.map((m) => (
              <li key={m.listing_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`my-listing-${m.listing_id}`}>
                <span className="min-w-0 truncate">{m.title} <span className="text-xs text-muted-foreground">· {formatMoney(m.price, cur)}</span></span>
                <span className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                    m.status === "active" ? "bg-emerald-500/15 text-emerald-400" :
                    m.status === "paused" ? "bg-amber-500/15 text-amber-400" :
                    m.status === "sold" ? "bg-primary/15 text-primary" :
                    "bg-red-500/15 text-red-400"}`}>{m.status}</span>
                  {m.status !== "active" && m.status !== "suspended" && (
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setStatus(m, "active")} data-testid={`reactivate-${m.listing_id}`}>
                      <Play className="mr-1 h-3 w-3" /> Re-activate
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 ? (
          <div className="col-span-3 rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            <ShoppingBag className="mx-auto h-8 w-8 opacity-40" />
            <p className="mt-3 font-display text-lg text-foreground">The marketplace is open — be the first to list!</p>
            <p className="mt-1 text-sm">List a service, product or skill in 60 seconds. Every member sees it instantly.</p>
            <Button className="mt-4" onClick={() => setNewOpen(true)} data-testid="empty-list-cta"><Plus className="mr-1 h-4 w-4" /> List something now</Button>
          </div>
        ) : items.map((it) => (
          <div key={it.listing_id} className={`flex flex-col overflow-hidden rounded-2xl border bg-card ${it.is_boosted ? "border-accent ring-1 ring-accent/40" : "border-border"}`} data-testid={`listing-${it.listing_id}`}>
            {it.image_url ? (
              <img src={it.image_url} alt={it.title} loading="lazy"
                className="h-36 w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="flex h-20 w-full items-center justify-center bg-gradient-to-br from-primary/15 via-card to-accent/10">
                <ShoppingBag className="h-6 w-6 text-muted-foreground/50" />
              </div>
            )}
            <div className="flex flex-1 flex-col p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col gap-1">
                <h3 className="font-display text-lg">{it.title}</h3>
                {it.is_boosted && (
                  <span className="inline-flex w-fit items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-medium text-accent" data-testid={`boosted-badge-${it.listing_id}`}>
                    <Sparkles className="h-3 w-3" /> Featured
                  </span>
                )}
              </div>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs capitalize">{it.category}</span>
            </div>
            <div className="mt-2 font-display text-2xl text-accent">{formatMoney(it.price, cur)}</div>
            <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{it.description}</p>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              by {it.seller_name}
              {it.seller_verified && (
                <span className="inline-flex items-center gap-0.5 text-primary" data-testid={`verified-badge-${it.listing_id}`}>
                  <BadgeCheck className="h-3.5 w-3.5" /> Verified
                </span>
              )}
              {it.review_count > 0 && (
                <span className="inline-flex items-center gap-0.5 text-amber-400" data-testid={`rating-${it.listing_id}`}>
                  <Star className="h-3 w-3 fill-amber-400" /> {it.avg_rating} ({it.review_count})
                </span>
              )}
              {it.location ? <span>· {it.location}</span> : null}
            </div>
            <div className="mt-4 flex flex-col gap-2">
              {user && it.user_id === user.user_id ? (
                <>
                  {!it.is_boosted && (
                    <Button onClick={() => boost(it)} variant="outline" data-testid={`boost-${it.listing_id}`}>
                      <Zap className="mr-1 h-4 w-4 text-accent" /> Feature at top · £2 (7 days)
                    </Button>
                  )}
                  <div className="flex gap-2">
                    <Button onClick={() => setStatus(it, "paused")} variant="outline" size="sm" className="flex-1" data-testid={`pause-${it.listing_id}`}>
                      <Pause className="mr-1 h-3.5 w-3.5" /> Pause
                    </Button>
                    <Button onClick={() => setStatus(it, "sold")} variant="outline" size="sm" className="flex-1" data-testid={`sold-${it.listing_id}`}>
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5 text-emerald-500" /> Sold
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Button onClick={() => buyNow(it)} data-testid={`buy-${it.listing_id}`}>
                    <CreditCard className="mr-1 h-4 w-4" /> Buy now · {formatMoney(it.price, cur)}
                  </Button>
                  <Button onClick={() => contact(it)} variant="outline" className="border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10" data-testid={`listing-contact-${it.listing_id}`}><MessageCircle className="mr-1 h-4 w-4" /> Contact seller</Button>
                  <button onClick={() => report(it)} className="self-end text-[11px] text-muted-foreground hover:text-red-400" data-testid={`report-${it.listing_id}`}>
                    <Flag className="mr-1 inline h-3 w-3" />Report listing
                  </button>
                </>
              )}
            </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!reviewFor} onOpenChange={(v) => !v && setReviewFor(null)}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Review: {reviewFor?.title}</DialogTitle>
            <DialogDescription>Honest reviews help other buyers trust the marketplace.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReview} className="space-y-4">
            <div className="flex justify-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`}>
                  <Star className={`h-8 w-8 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                </button>
              ))}
            </div>
            <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What was great (or not)?" className="h-11" data-testid="review-comment" />
            <Button type="submit" className="h-11 w-full" data-testid="review-submit">Post review</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent data-testid="verify-dialog">
          <DialogHeader>
            <DialogTitle>Seller verification</DialogTitle>
            <DialogDescription>Required for the Verified badge. Your details stay private and are only used for identity checks (UK platform rules).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitVerify} className="space-y-3">
            <div className="space-y-1.5"><Label>Full legal name</Label><Input value={vForm.full_name} onChange={(e) => setVForm({ ...vForm, full_name: e.target.value })} required className="h-11" data-testid="verify-name" /></div>
            <div className="space-y-1.5"><Label>Date of birth</Label><Input type="date" value={vForm.date_of_birth} onChange={(e) => setVForm({ ...vForm, date_of_birth: e.target.value })} required className="h-11" data-testid="verify-dob" /></div>
            <div className="space-y-1.5"><Label>Address</Label><Input value={vForm.address} onChange={(e) => setVForm({ ...vForm, address: e.target.value })} required className="h-11" data-testid="verify-address" /></div>
            <div className="space-y-1.5"><Label>Business name (optional)</Label><Input value={vForm.business_name} onChange={(e) => setVForm({ ...vForm, business_name: e.target.value })} className="h-11" /></div>
            <Button type="submit" className="h-11 w-full" data-testid="verify-submit">Submit for review</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="market-create-dialog">
          <DialogHeader><DialogTitle>List a product or service</DialogTitle><DialogDescription>Buyers will contact you on WhatsApp directly.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="h-11" data-testid="market-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="h-11" data-testid="market-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.filter((c) => c !== "all").map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Price ({cur})</Label><Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required className="h-11" data-testid="market-price" /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required minLength={10} rows={3} data-testid="market-description" /></div>
            <div className="space-y-1.5"><Label>WhatsApp number (with country code)</Label><Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} required className="h-11" placeholder="447587831674" data-testid="market-whatsapp" /></div>
            <div className="space-y-1.5"><Label>Location (optional)</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="h-11" /></div>
            <div className="space-y-1.5"><Label>Photo URL (optional)</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="h-11" placeholder="https://… link to a product photo" data-testid="market-image-url" /></div>
            <Button type="submit" className="h-11 w-full" data-testid="market-submit">Publish listing</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
